import { Component, inject, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { confirmLinkRemoval } from '../../../../../../../shared/helpers/link-removal';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, map, distinctUntilChanged, take } from 'rxjs/operators';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../shared/services';
import { LocationPickerLauncherService } from '../../../../../../../shared/services/location-picker-launcher.service';
import { mergePickedLocation, recordLinkPayload } from '../../../../../../../shared/helpers/design-context';
import { SheetMarkupQueueService } from '../../../../../../../shared/services/sheet-markup-queue.service';
import { MapFeatureLink } from '../../../../../../../shared/models/map-feature-link';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  CreateWarrantyClaimRequest,
  CreateWarrantyClaimSuccess,
  FetchWarrantyClaimsRequest,
  FetchLineageRequest,
} from '../../../../../core/projects.actions';
import { lineageList, projectDetails } from '../../../../../core/projects.selectors';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { UploadFileRequest } from '../../../../../../../shared/core/shared.actions';
import { issueStatusText } from '../../../../../../../shared/helpers/issue-workflow';

@Component({
  selector: 'app-warranty-claim-form',
  templateUrl: './warranty-claim-form.component.html',
  styleUrls: ['./warranty-claim-form.component.scss'],
  standalone: false,
})
export class WarrantyClaimFormComponent implements OnInit, OnDestroy {
  /** A linked issue's status as its badge prints it (a contractor-workflow stage in words). */
  readonly issueStatusText = issueStatusText;

  @ViewChild('fileInput', { static: false }) fileInputRef: ElementRef;
  @Input() data: any;
  form: FormGroup;
  isFormDirty = false;

  errorMessages: Record<string, string> = {};

  mediaFiles: any[] = [];
  latitude: number = null;
  longitude: number = null;
  /**
   * Design context (spec §12). Like lat/lng, a component field: undefined while untouched on a claim that has no
   * link, so a project without design layers sends exactly today's payload (no `map_feature_link` key).
   */
  mapFeatureLink: MapFeatureLink | null | undefined = undefined;
  selectedTabIndex = 0;
  workflowHistory: any[] = [];

  structureTypeOptions: any[] = [];
  claimTypeOptions: any[] = [];
  defectTypeOptions: any[] = [];
  defectTypeMode: 'dropdown' | 'text' | 'hidden' = 'hidden';

  severityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  warrantyOptions: any[] = [];
  private orgId: string;

  // Sites for the SITE picklist
  sitesList: any[] = [];
  private sitesById: Record<string, any> = {};
  // True once the user has manually pinned/edited the location, so site changes
  // shouldn't overwrite their lat/lon.
  private locationManuallySet = false;

  // Nearby issues
  nearbyIssues: any[] = [];
  nearbyIssuesLoading = false;
  selectedIssueIds: Set<number> = new Set();

  // Linked issues (when editing an existing claim)
  linkedIssues: any[] = [];
  linkedIssuesLoading = false;

  private locationChanged$ = new Subject<{ lat: number; lng: number }>();

  addMenuOptions = [
    {
      key: 'picture',
      icon: 'paperclip',
      label: 'Attachments',
      handler: () => {
        this.fileInputRef.nativeElement.click();
      },
    },
    {
      key: 'location',
      icon: 'marker-outline',
      label: 'Location',
      handler: () => {
        this.openLocationPicker();
      },
    },
  ];

  private readonly onDestroy = new Subject<void>();
  private readonly locationPickerLauncher = inject(LocationPickerLauncherService);
  /** Picker strokes held for a sheet link that exists only once this record saves. */
  private readonly sheetMarkups = inject(SheetMarkupQueueService);

  constructor(
    private store: Store,
    private actions$: Actions,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    private dialog: MatDialog,
    private appHttpService: AppHttpService,
  ) {
    this.form = new FormGroup({
      warranty_id: new FormControl('', Validators.required),
      site_id: new FormControl('', Validators.required),
      structure_type: new FormControl(''),
      claim_type: new FormControl(''),
      defect_type: new FormControl(''),
      title: new FormControl('', Validators.required),
      description: new FormControl(''),
      proposed_solution: new FormControl(''),
      severity: new FormControl(''),
      need_by_date: new FormControl(''),
    });
  }

  fetchLineage() {
    if (!this.data?.editClaim?.id) return;
    this.store.dispatch(FetchLineageRequest({
      payload: {
        qp: {
          start: 0,
          total: 1000,
          ex: `warranty-claim/${this.data.editClaim.id}`,
          include: ['actor', 'state', 'attachments'],
        },
      },
    }));
  }

  getInitials(user: any): string {
    if (!user) return '?';
    return (user.first_name?.[0] || '') + (user.last_name?.[0] || '');
  }

  ngOnInit() {
    // Get org ID for lookup API calls
    const account = this.authService.getCurrentUser();
    this.orgId = account?.organization?.id || '';

    // Fetch org-level lookups
    this.fetchLookup('warranty_structure_type').then(options => this.structureTypeOptions = options);
    this.fetchLookup('warranty_claim_type').then(options => this.claimTypeOptions = options);

    // Listen for claim_type changes to update defect_type field
    this.form.get('claim_type')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(claimType => {
        this.form.get('defect_type')?.setValue('');
        this.defectTypeOptions = [];
        this.updateDefectTypeField(claimType);
      });

    // Fetch lineage and subscribe for Discussion tab (when editing)
    if (this.data?.editClaim?.id) {
      this.fetchLineage();
      this.store.select(lineageList)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(data => {
          if (data?.length) {
            const cloned = JSON.parse(JSON.stringify(data));
            this.workflowHistory = cloned.map(step => ({
              action: step?.state?.comment || step?.state?.name || 'Action',
              actor_name: step?.actor ? `${step.actor.first_name} ${step.actor.last_name}` : 'Unknown',
              actor_initials: this.getInitials(step?.actor),
              date: step?.created_at,
              notes: step?.input?.formValues?.comments || null,
            }));
          }
        });
    }

    // Build warranty options from data
    if (this.data?.warranties?.length) {
      this.warrantyOptions = this.data.warranties.map(w => ({
        value: w.id,
        label: (w.attributes?.name || w.name || w.id) + (w.attributes?.status ? ` (${w.attributes.status})` : ''),
      }));
      // Auto-select if only one warranty
      if (this.warrantyOptions.length === 1) {
        this.form.patchValue({ warranty_id: this.warrantyOptions[0].value });
      }
    }

    // Pre-populate form when editing a draft claim
    if (this.data?.editClaim) {
      const c = this.data.editClaim;
      this.form.patchValue({
        warranty_id: c.warranty_id || c.warranty?.id || '',
        site_id: c.site?.id || c.site_id || '',
        structure_type: c.structure_type || '',
        claim_type: c.claim_type || '',
        title: c.title || '',
        description: c.description || '',
        proposed_solution: c.proposed_solution || '',
        severity: c.severity || 'medium',
        need_by_date: c.need_by_date || '',
      });
      // After patching claim_type, load defect type options then patch defect_type
      if (c.claim_type) {
        this.updateDefectTypeField(c.claim_type).then(() => {
          if (c.defect_type) {
            this.form.patchValue({ defect_type: c.defect_type });
          }
        });
      }
      this.latitude = c.latitude || null;
      this.longitude = c.longitude || null;
      if (c.map_feature_link) this.mapFeatureLink = c.map_feature_link;
      // If the saved claim already has lat/lon, treat it as a manual pin so
      // changing the SITE picker won't silently overwrite it.
      if (this.latitude !== null && this.longitude !== null) {
        this.locationManuallySet = true;
      }
      const normalize = (item: any, defaultType: 'picture' | 'document') => ({
        ...item,
        fileType: item?.fileType
          || (item?.mime?.startsWith('image/') ? 'picture'
            : item?.mime?.startsWith('video/') ? 'video'
            : defaultType),
      });
      const pics = (c.pictures || []).map((p: any) => normalize(p, 'picture'));
      const docs = (c.attachments || []).map((a: any) => normalize(a, 'document'));
      const merged = [...pics, ...docs];
      if (merged.length) {
        this.mediaFiles = JSON.parse(JSON.stringify(merged));
        setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
      }
    }

    // Subscribe to projectDetails to populate the SITE picklist
    this.store.select(projectDetails)
      .pipe(
        takeUntil(this.onDestroy),
        map(project => project?.sites || []),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      )
      .subscribe((sites: any[]) => {
        this.sitesList = sites.map(s => ({ value: s.id, label: s.name }));
        this.sitesById = sites.reduce((acc, s) => {
          acc[s.id] = s;
          return acc;
        }, {} as Record<string, any>);
      });

    // When the SITE picker changes, default the claim's lat/lon to that site
    // unless the user has manually pinned a location already.
    this.form.get('site_id')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(siteId => {
        if (!siteId) return;
        const site = this.sitesById[siteId];
        if (site && !this.locationManuallySet) {
          const lat = site.latitude ?? site.lat ?? null;
          const lng = site.longitude ?? site.lng ?? site.lon ?? null;
          if (lat !== null && lng !== null) {
            this.latitude = Number(lat);
            this.longitude = Number(lng);
          }
        }
        // Re-fetch nearby issues for the new site (with current lat/lng)
        if (this.latitude !== null && this.longitude !== null) {
          this.locationChanged$.next({ lat: this.latitude, lng: this.longitude });
        }
      });

    // Debounced nearby issues fetch when location changes
    this.locationChanged$
      .pipe(takeUntil(this.onDestroy), debounceTime(500))
      .subscribe(({ lat, lng }) => {
        this.fetchNearbyIssues(lat, lng);
      });

    // If editing and location already exists, fetch nearby issues
    if (this.data?.editClaim && this.latitude !== null && this.longitude !== null) {
      this.fetchNearbyIssues(this.latitude, this.longitude);
      this.fetchLinkedIssues(this.data.editClaim.id);
    }

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.isFormDirty = this.form.dirty;
      });

    this.actions$.pipe(
      ofType(CreateWarrantyClaimSuccess),
      takeUntil(this.onDestroy),
    ).subscribe((action: any) => {
      // After creating a new claim, link selected issues if any
      const newClaimId = action?.data?.id;
      if (newClaimId && this.selectedIssueIds.size > 0) {
        this.linkIssuesToClaim(newClaimId).subscribe({
          next: () => this.projectsService.closeRightPanel.next(),
          error: () => this.projectsService.closeRightPanel.next(),
        });
      } else {
        this.projectsService.closeRightPanel.next();
      }
    });

    // Listen for uploaded files (deep clone to avoid NgRx frozen object errors)
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
        const newFiles = items.filter(
          (item: any) => item?.fileType === 'picture' || item?.fileType === 'document',
        );
        if (newFiles.length) {
          this.mediaFiles = [...this.mediaFiles, ...newFiles];
          this.form.markAsDirty();
          this.isFormDirty = true;
          // Notify pictures-section-with-tags component via mediaData subject
          setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
        }
      });
  }

  /**
   * Fetch org-level lookup values and return as dropdown options.
   */
  private fetchLookup(type: string): Promise<any[]> {
    if (!this.orgId) return Promise.resolve([]);
    return this.appHttpService
      .getService(`/v1/lookup/${type}`, { entity_type: 'organization', entity_id: this.orgId })
      .toPromise()
      .then((resp: any) => {
        const items = resp?.data || resp || [];
        return (Array.isArray(items) ? items : []).map((item: any) => ({
          value: item.value || item.name || item,
          label: item.value || item.name || item,
        }));
      })
      .catch(() => []);
  }

  /**
   * Update defect_type field mode and options based on the selected claim_type.
   */
  private updateDefectTypeField(claimType: string): Promise<void> {
    if (!claimType) {
      this.defectTypeMode = 'hidden';
      this.defectTypeOptions = [];
      return Promise.resolve();
    }

    // Concrete and Asphalt have curated defect lists. Every other claim type --
    // the catch-all ('Other'/'Others') and any org-specific type such as
    // Greeley's Landscape and Steel -- takes a free-text defect instead of
    // being hidden, so the inspector can still record what is wrong.
    const lower = claimType.toLowerCase();
    if (lower === 'concrete') {
      this.defectTypeMode = 'dropdown';
      return this.fetchLookup('warranty_defect_type_concrete').then(options => {
        this.defectTypeOptions = options;
      });
    } else if (lower === 'asphalt') {
      this.defectTypeMode = 'dropdown';
      return this.fetchLookup('warranty_defect_type_asphalt').then(options => {
        this.defectTypeOptions = options;
      });
    } else {
      this.defectTypeMode = 'text';
      this.defectTypeOptions = [];
      return Promise.resolve();
    }
  }

  checkForErrors(event: any) {
    if (event?.controlName && event?.error) {
      this.errorMessages[event.controlName] = event.error;
    }
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    const imageFormData = new FormData();

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file.type.startsWith('image/') || file.type.startsWith('application/') || file.type.startsWith('text/')) {
        imageFormData.append('inputFiles', file);
      }
    }

    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }

    this.fileInputRef.nativeElement.value = '';
  }

  deletePicture(pic: any) {
    this.mediaFiles = this.mediaFiles.filter(p => p.id !== pic.id);
    this.form.markAsDirty();
    setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
  }

  /**
   * The design-context picker on projects with published layers, else today's Google picker (the launcher decides).
   * Resolves once the picked location is applied.
   */
  async openLocationPicker(): Promise<void> {
    let ref;
    try {
      ref = await this.locationPickerLauncher.open({
        latitude: this.latitude,
        longitude: this.longitude,
        showIncludeGpsLocationCheckbox: false,
        existingLink: this.mapFeatureLink ?? null,
        recordLabel: 'warranty claim',
      });
    } catch (error) {
      console.error('Location picker failed to open', error);
      return;
    }
    const closed = ref?.afterClosed?.();
    if (!closed) return;
    await new Promise<void>(resolve => closed.pipe(take(1)).subscribe({
      next: resp => { this.applyPickedLocation(resp); resolve(); },
      complete: () => resolve(),
      error: () => resolve(),
    }));
  }

  private applyPickedLocation(resp: unknown): void {
    if (!resp) return;
    const patch = mergePickedLocation(resp);
    if ('map_feature_link' in patch) {
      this.mapFeatureLink = patch.map_feature_link;
      this.form.markAsDirty();
      this.isFormDirty = true;
    }
    if ('latitude' in patch && 'longitude' in patch) {
      this.latitude = patch.latitude as number;
      this.longitude = patch.longitude as number;
      // User has now manually pinned a location; don't overwrite from SITE picker.
      this.locationManuallySet = true;
      this.form.markAsDirty();
      this.isFormDirty = true;
      // Trigger nearby issues fetch
      if (this.latitude !== null && this.longitude !== null) {
        this.locationChanged$.next({ lat: this.latitude, lng: this.longitude });
      }
    }
  }

  /** The card's "Remove link": the pin stays. */
  removeDesignContext(): void {
    this.mapFeatureLink = null;
    this.form.markAsDirty();
    this.isFormDirty = true;
  }

  /** A snapshot retry / finished render / saved markup: not an unsaved edit. */
  onDesignContextLinkChange(link: MapFeatureLink): void {
    this.mapFeatureLink = link;
  }

  deleteLocation() {
    // W77: the link goes with the pin, so markup on it asks first
    const link = this.mapFeatureLink;
    const sheet = link?.sheet;
    const held = !!sheet && !link.uuid && !!this.sheetMarkups.heldFor(link);
    confirmLinkRemoval(this.dialog, link, held).subscribe(go => {
      if (!go) return;
      this.latitude = null;
      this.longitude = null;
      // A link whose pin is gone would keep pointing at the old spot.
      if (this.mapFeatureLink) this.mapFeatureLink = null;
      this.nearbyIssues = [];
      this.selectedIssueIds.clear();
      // Reset the manual flag so a SITE selection can repopulate the pin.
      this.locationManuallySet = false;
      this.form.markAsDirty();
    });
  }

  /**
   * True when the warranty selected on this claim is active. Claims can only be
   * created/updated against an active warranty; used to gate the Save/Submit
   * buttons and mirror the server-side guard.
   */
  isWarrantyActive(): boolean {
    const selectedId = this.form?.value?.warranty_id
      ?? this.data?.editClaim?.warranty_id
      ?? this.data?.editClaim?.warranty?.id;
    const match = (this.data?.warranties || []).find(
      (w: any) => (w.id ?? w.attributes?.id) === selectedId,
    );
    const status = match?.attributes?.status
      ?? match?.status
      ?? this.data?.editClaim?.warranty?.status;
    // If we can't resolve a status, don't block the user — the server still guards.
    return status == null || status === 'active';
  }

  /**
   * True while the claim is still being authored, i.e. the form owns its status
   * (Save keeps it a draft, Submit promotes it to submitted). A new claim counts
   * as authoring. Once a claim is in flight (submitted / sent_to_contractor /
   * responded / verified) a PM may still correct its details, but the form must
   * leave the status alone -- only the workflow actions move it.
   */
  isAuthoringClaim(): boolean {
    const status = this.data?.editClaim?.status;
    return !this.data?.editClaim?.id || ['draft', 'sent_back'].includes(status);
  }

  save() {
    if (!this.guardWarrantyActive()) return;
    if (this.data?.editClaim?.id) {
      // Editing an in-flight claim: keep the status the workflow put it in.
      this._update(this.isAuthoringClaim() ? 'draft' : null);
    } else {
      this._create('draft');
    }
  }

  submit() {
    if (!this.guardWarrantyActive()) return;
    if (this.data?.editClaim?.id) {
      this._update('submitted');
    } else {
      this._create('submitted');
    }
  }

  private guardWarrantyActive(): boolean {
    if (this.isWarrantyActive()) return true;
    this.commonService.notification(
      'Warranty is not active — claims cannot be created or updated.',
      'error',
    );
    return false;
  }

  /** @param status pass null to leave the claim's current status untouched. */
  private buildPayload(status: string | null) {
    const { warranty_id, ...claimData } = this.form.value;
    return {
      ...claimData,
      // Keep stripping warranty_id (URL param), but include site_id from the form.
      site_id: this.form.value.site_id,
      ...(status === null ? {} : { status }),
      pictures: this.mediaFiles.map(p => p.id),
      latitude: this.latitude,
      longitude: this.longitude,
      ...this.mapFeatureLinkPayload(),
    };
  }

  /** Tri-state: omitted while untouched (no layers → today's payload), null clears, else the api input shape. */
  private mapFeatureLinkPayload() {
    // undefined until a link is loaded or picked, so null is always a removal made here: { sheet: null } (R54)
    const link = recordLinkPayload(this.mapFeatureLink, this.latitude, this.longitude, this.mapFeatureLink === null);
    return link === undefined ? {} : { map_feature_link: link };
  }

  private _create(status: string) {
    if (!this.form.valid || !this.data?.projectId) return;
    const warrantyId = this.form.value.warranty_id;
    if (!warrantyId) return;

    this.store.dispatch(CreateWarrantyClaimRequest({
      payload: {
        projectId: this.data.projectId,
        warrantyId,
        data: this.buildPayload(status),
      },
    }));
  }

  private _update(status: string | null) {
    if (!this.form.valid) return;
    const claimId = this.data.editClaim.id;
    const warranty_id = this.form.value.warranty_id;

    this.appHttpService.patchService(
      `/v1/warranty/claims/${claimId}`,
      this.buildPayload(status),
    ).subscribe({
      next: (resp: unknown) => {
        void this.sheetMarkups.flush(resp);
        // Link selected issues if any
        if (this.selectedIssueIds.size > 0) {
          this.linkIssuesToClaim(claimId).subscribe({
            next: () => this._closeAndRefresh(warranty_id),
            error: () => this._closeAndRefresh(warranty_id),
          });
        } else {
          this._closeAndRefresh(warranty_id);
        }
      },
      error: (err: any) => {
        const message = err?.error?.message
          || err?.error?.error
          || 'Failed to update claim. The warranty may no longer be active.';
        this.commonService.notification(message, 'error');
      },
    });
  }

  private _closeAndRefresh(warrantyId: string) {
    this.projectsService.closeRightPanel.next();
    if (this.data?.projectId && warrantyId) {
      this.store.dispatch(FetchWarrantyClaimsRequest({
        payload: { projectId: this.data.projectId, warrantyId },
      }));
    }
  }

  deleteClaim() {
    if (!this.data?.editClaim?.id) return;
    const claimNumber = this.data.editClaim.claim_number || 'this claim';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete claim?',
        `<b>${claimNumber}</b> will be deleted. This cannot be undone.`,
        'Delete',
        'Cancel',
      ),
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      const claimId = this.data.editClaim.id;
      const warrantyId = this.data.editClaim.warranty?.id || this.data.editClaim.warranty_id;
      this.appHttpService.deleteService(`/v1/warranty/claims/${claimId}`).subscribe(() => {
        this.commonService.notification(`${claimNumber} deleted`, 'success');
        this.projectsService.closeRightPanel.next();
        if (this.data?.projectId && warrantyId) {
          this.store.dispatch(FetchWarrantyClaimsRequest({
            payload: { projectId: this.data.projectId, warrantyId },
          }));
        }
      });
    });
  }

  // --- Nearby Issues ---

  fetchNearbyIssues(lat: number, lng: number) {
    if (!this.data?.projectId) return;
    this.nearbyIssuesLoading = true;
    const siteId = this.form.value.site_id || undefined;
    this.appHttpService.getService(
      `/v1/warranty/project/${this.data.projectId}/nearby-issues`,
      { lat, lng, radius: 200, type: 'Warranty', claim_id: this.data?.editClaim?.id || undefined, site_id: siteId },
    ).subscribe({
      next: (resp: any) => {
        this.nearbyIssues = resp?.data || resp || [];
        this.nearbyIssuesLoading = false;
      },
      error: () => {
        this.nearbyIssues = [];
        this.nearbyIssuesLoading = false;
      },
    });
  }

  fetchLinkedIssues(claimId: number) {
    this.linkedIssuesLoading = true;
    this.appHttpService.getService(`/v1/warranty/claims/${claimId}/issues`).subscribe({
      next: (resp: any) => {
        this.linkedIssues = resp?.data || resp || [];
        this.linkedIssuesLoading = false;
      },
      error: () => {
        this.linkedIssues = [];
        this.linkedIssuesLoading = false;
      },
    });
  }

  toggleIssueSelection(issueId: number) {
    if (this.selectedIssueIds.has(issueId)) {
      this.selectedIssueIds.delete(issueId);
    } else {
      this.selectedIssueIds.add(issueId);
    }
    this.form.markAsDirty();
    this.isFormDirty = true;
  }

  isIssueSelected(issueId: number): boolean {
    return this.selectedIssueIds.has(issueId);
  }

  isIssueLinked(issueId: number): boolean {
    return this.linkedIssues.some(li => li.id === issueId);
  }

  unlinkIssue(issue: any) {
    if (!this.data?.editClaim?.id) return;
    const claimId = this.data.editClaim.id;
    this.appHttpService.deleteService(`/v1/warranty/claims/${claimId}/issues/${issue.id}`).subscribe({
      next: () => {
        this.linkedIssues = this.linkedIssues.filter(li => li.id !== issue.id);
        // Also un-disable in the nearby list by refreshing linked_to_claim
        const nearbyMatch = this.nearbyIssues.find(ni => ni.id === issue.id);
        if (nearbyMatch) {
          nearbyMatch.linked_to_claim = false;
        }
      },
    });
  }

  formatDistance(meters: number): string {
    if (meters == null) return '';
    if (meters < 1000) return `${Math.round(meters)}m away`;
    return `${(meters / 1000).toFixed(1)}km away`;
  }

  private linkIssuesToClaim(claimId: number) {
    const issueIds = Array.from(this.selectedIssueIds);
    return this.appHttpService.postService(
      `/v1/warranty/claims/${claimId}/issues`,
      { issue_ids: issueIds },
    );
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
